import { useContext, useState, MouseEvent } from "react";
import { Box, Typography, Popover, Card, Grid, Divider } from "@mui/material";
import { UserContext } from "../contexts/userContext";
import { getIsImpersonate } from "../utils/storage";
import { IUserResponses } from "../interfaces/userContext";
import { COUNTRIES, DEFAULT_COUNTRY } from "../utils/constants";
import { Button } from "./common/Button";
import { useImpersonation } from "../customHooks/useImpersonation";
import { ExitToApp, Info } from "@mui/icons-material";

const ImpersonateUserCard = ({
  currentUser,
}: {
  currentUser: IUserResponses;
}) => {
  const entityTypeLabel: { [key: string]: string } = {
    marketplace: "Marketplace",
    merchant: "Merchant",
    ecommerce: "E-commerce",
    internal: "Interno",
  };

  const rolesDisplay = currentUser?.roles?.map((role) => role.name).join(", ");

  const renderColumn = (title: string, content: string) => (
    <Box display="flex" flexDirection="column">
      <Typography className="f-s-16 text-bold" marginBottom={0.5}>
        {title}
      </Typography>
      <Typography className="f-s-14">
        {content}
      </Typography>
    </Box>
  );

  return (
    <Grid
      component={Card}
      className="br-10 bg-light"
      item
      container
      xs={12}
      justifyContent="space-between"
      alignItems="flex-start"
      sx={{ padding: 2.5, width: 500 }}
      spacing={1.5}
    >
      <Grid item xs={6}>
        {renderColumn("Nombre", currentUser?.name || "-")}
      </Grid>
      <Grid item xs={6}>
        {renderColumn("Email", currentUser?.email || "-")}
      </Grid>
      <Grid item xs={6}>
        {renderColumn("User ID", String(currentUser?.id) || "-")}
      </Grid>
      <Grid item xs={6}>
        {renderColumn("Rol(es)", rolesDisplay || "-")}
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={6}>
        {renderColumn(
          "Entidad",
          entityTypeLabel[currentUser?.entity] || currentUser?.entity,
        )}
      </Grid>
      <Grid item xs={6}>
        {renderColumn("Alias", currentUser?.entityInfo?.name || "-")}
      </Grid>
      <Grid item xs={6}>
        {renderColumn("CUIT", currentUser?.entityInfo?.cuit || "-")}
      </Grid>
      <Grid item xs={6}>
        {renderColumn(
          "Razón Social",
          currentUser?.entityInfo?.businessName || "-",
        )}
      </Grid>
      <Grid item xs={6}>
        {renderColumn(
          "País",
          COUNTRIES[currentUser?.entityInfo?.country || DEFAULT_COUNTRY],
        )}
      </Grid>
    </Grid>
  );
};

const ImpersonateHeader = () => {
  const { currentUser } = useContext(UserContext);
  const { stopImpersonation } = useImpersonation();
  const isImpersonate = getIsImpersonate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  if (!isImpersonate || !currentUser.name) return null;

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: "36px",
          backgroundColor: "primary.main",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          zIndex: 9999,
        }}
      >
        <Box
          onMouseEnter={handleOpen}
          className="d-flex pointer"
          sx={{ alignItems: "center", gap: 1 }}
        >
          <Info sx={{ color: "white", fontSize: "1rem" }} />
          <Typography className="f-s-14 text-white">
            Sesión activa como:
            <span className="text-bold">
              {currentUser?.name || currentUser?.email}
            </span>
          </Typography>
        </Box>

        <Button
          variant="contained"
          size="small"
          endIcon={<ExitToApp />}
          onClick={stopImpersonation}
          sx={{
            backgroundColor: "white",
            color: "primary.main",
            fontWeight: 600,
            fontSize: "0.75rem",
            padding: "4px 12px",
            "&:hover": {
              backgroundColor: "#f5f5f5",
            },
          }}
        >
          Salir
        </Button>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          onMouseLeave: handleClose,
          sx: { marginTop: "10px" },
        }}
      >
        <ImpersonateUserCard currentUser={currentUser} />
      </Popover>
    </>
  );
};

export default ImpersonateHeader;
